import React, { useCallback, useEffect, useRef, useState } from 'react';
import ForceGraph2D, { ForceGraphMethods, NodeObject } from 'react-force-graph-2d';
import { performanceMetricsService } from '../../../../services/performanceMetricsService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MiniNode extends NodeObject {
  id: string;
  title: string;
  isCurrent: boolean;
  clicks: number;
  direction: 'current' | 'incoming' | 'outgoing' | 'both';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const GRAPH_WIDTH = 460;
const GRAPH_HEIGHT = 280;

const normalizeUrl = (u: string): string => {
  if (!u) return u;
  const stripped = u.replace(/\/+$/, '');
  return stripped || u;
};

// ---------------------------------------------------------------------------
// Section wrapper (local, same as in ArticleDetailPanel)
// ---------------------------------------------------------------------------

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{title}</h3>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// NeighborhoodGraph
// ---------------------------------------------------------------------------

export const NeighborhoodGraph: React.FC<{ url: string }> = ({ url }) => {
  const graphRef = useRef<ForceGraphMethods | undefined>();
  const [graphData, setGraphData] = useState<{ nodes: MiniNode[]; links: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [neighborCount, setNeighborCount] = useState(0);

  const norm = normalizeUrl(url);

  useEffect(() => {
    (async () => {
      try {
        const full = await performanceMetricsService.getGraphLinks();

        // Find the current node + all 1-hop neighbors with direction
        const incomingIds = new Set<string>();  // articles that link TO this one
        const outgoingIds = new Set<string>();  // articles this one links TO
        for (const edge of full.edges) {
          const src = normalizeUrl(edge.source);
          const tgt = normalizeUrl(edge.target);
          if (src === norm) outgoingIds.add(tgt);
          if (tgt === norm) incomingIds.add(src);
        }

        const allNodes = [...full.nodes, ...full.orphans];
        const nodeById = new Map(allNodes.map((n) => [normalizeUrl(n.id), n]));

        const allNeighborIds = new Set([norm, ...Array.from(incomingIds), ...Array.from(outgoingIds)]);
        const nodes: MiniNode[] = [];
        for (const nid of Array.from(allNeighborIds)) {
          const n = nodeById.get(nid);
          if (n) {
            const isIncoming = incomingIds.has(nid);
            const isOutgoing = outgoingIds.has(nid);
            nodes.push({
              id: n.id,
              title: n.title,
              isCurrent: nid === norm,
              clicks: n.clicks,
              direction: nid === norm ? 'current'
                : isIncoming && isOutgoing ? 'both'
                : isIncoming ? 'incoming' : 'outgoing',
            });
          }
        }

        const nodeIdSet = new Set(nodes.map((n) => normalizeUrl(n.id)));
        const links = full.edges
          .filter((e) => nodeIdSet.has(normalizeUrl(e.source)) && nodeIdSet.has(normalizeUrl(e.target)))
          .map((e) => ({ source: e.source, target: e.target, anchors: e.anchors }));

        setNeighborCount(nodes.length - 1);
        setGraphData({ nodes, links });
      } catch {
        setGraphData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [norm]);

  const paintNode = useCallback((node: NodeObject, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const n = node as MiniNode;
    const r = n.isCurrent ? 8 : Math.max(3, Math.min(6, 2 + Math.log10((n.clicks || 1) + 1) * 2));
    const x = n.x ?? 0;
    const y = n.y ?? 0;

    // Colors by direction: incoming=green, outgoing=blue, both=purple, current=cyan
    const fillColor = n.isCurrent ? '#01bcd4'
      : n.direction === 'incoming' ? '#10b981'
      : n.direction === 'outgoing' ? '#3b82f6'
      : n.direction === 'both' ? '#8b5cf6'
      : '#94a3b8';

    ctx.beginPath();
    ctx.arc(x, y, r, 0, 2 * Math.PI);
    ctx.fillStyle = fillColor;
    ctx.globalAlpha = n.isCurrent ? 1 : 0.8;
    ctx.fill();
    ctx.globalAlpha = 1;

    if (n.isCurrent) {
      ctx.strokeStyle = '#0e7490';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Labels
    const label = (n.title || '').replace(/&#038;/g, '&').replace(/&amp;/g, '&').slice(0, 25);
    const fontSize = n.isCurrent ? Math.max(4, 11 / globalScale) : Math.max(3, 9 / globalScale);
    ctx.font = `${n.isCurrent ? 'bold ' : ''}${fontSize}px sans-serif`;
    ctx.fillStyle = n.isCurrent ? '#0e7490' : '#64748b';
    ctx.textAlign = 'center';
    ctx.fillText(label, x, y + r + 10 / globalScale);
  }, []);

  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  if (loading) {
    return (
      <Section title="Link structure">
        <div className="flex items-center justify-center h-20">
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-cyan-500" />
        </div>
      </Section>
    );
  }

  if (!graphData || graphData.nodes.length <= 1) {
    return (
      <Section title="Link structure">
        <div className="text-xs text-amber-600 bg-amber-50 rounded px-3 py-2">
          No internal links to or from other blog posts. This article is isolated in the link graph.
        </div>
      </Section>
    );
  }

  return (
    <Section title={`Link structure (${neighborCount} connected article${neighborCount !== 1 ? 's' : ''})`}>
      <div className="relative border border-gray-200 rounded bg-gray-50 overflow-hidden">
        <ForceGraph2D
          ref={graphRef}
          width={GRAPH_WIDTH}
          height={GRAPH_HEIGHT}
          graphData={graphData}
          nodeCanvasObject={paintNode}
          nodePointerAreaPaint={(node, color, ctx) => {
            const n = node as MiniNode;
            const r = (n.isCurrent ? 8 : 6) + 2;
            ctx.beginPath();
            ctx.arc(n.x ?? 0, n.y ?? 0, r, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
          }}
          linkColor={() => 'rgba(148, 163, 184, 0.5)'}
          linkWidth={() => 1}
          linkDirectionalArrowLength={4}
          linkDirectionalArrowRelPos={0.85}
          onNodeHover={(node) => {
            if (!node) { setTooltip(null); return; }
            const n = node as MiniNode;
            const screen = graphRef.current?.graph2ScreenCoords(n.x ?? 0, n.y ?? 0);
            if (screen) {
              setTooltip({ x: screen.x + 15, y: screen.y - 10, text: (n.title || '').replace(/&#038;/g, '&').replace(/&amp;/g, '&') });
            }
          }}
          d3AlphaDecay={0.05}
          d3VelocityDecay={0.4}
          warmupTicks={50}
          cooldownTicks={100}
        />
        {tooltip && (
          <div className="absolute z-10 bg-white shadow border border-gray-200 rounded px-2 py-1 text-xs text-gray-900 pointer-events-none max-w-[200px] truncate" style={{ left: tooltip.x, top: tooltip.y }}>
            {tooltip.text}
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 mt-1.5 text-[10px] text-gray-400">
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-cyan-500" /> This article</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-green-500" /> Links to this</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-blue-500" /> Linked from this</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-purple-500" /> Both</span>
      </div>
    </Section>
  );
};
